// src/pages/qc/PageQCFgNc.jsx
import { useState, useEffect, useMemo } from "react";
import * as api from "../../apiService";
import { downloadPdf } from "../../apiService";
import { FgNcCreateModal } from "./FgNcCreateModal";
import { FgNcDetailModal } from "./FgNcDetailModal";
import { fmtDate, currentMonthKey } from "../../utils/dateUtils";
import { MonthYearSelect } from "../../components/MonthYearSelect";
import { useToast } from "../../components/Toast";

const STATUS_STYLE = {
  OPEN:                 { bg: "#fef3c7", color: "#92400e" },
  PRODUCTION_RESPONDED: { bg: "#dbeafe", color: "#1e40af" },
  CLOSED:               { bg: "#d1fae5", color: "#065f46" },
};

const STATUS_LABEL = {
  OPEN:                 "Open",
  PRODUCTION_RESPONDED: "Production Responded",
  CLOSED:               "Closed",
};

const DISPOSITION_LABELS = {
  APPROVE:               "Approve",
  APPROVE_CONDITIONALLY: "Approve Conditionally",
  REWORK:                "Rework",
  REJECT_RECYCLE:        "Reject & Recycle",
  DESTROY:               "Destroy",
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] ?? { bg: "#f3f4f6", color: "#374151" };
  return (
    <span style={{ background: s.bg, color: s.color, borderRadius: 4,
      padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>
      {STATUS_LABEL[status] ?? status}
    </span>
  );
}


export function PageQCFgNc({ myPerms }) {
  const showToast = useToast();
  const [rows,     setRows]    = useState([]);
  const [loading,  setLoading] = useState(true);
  const [error,    setError]   = useState(null);
  const [search,   setSearch]   = useState("");
  const [monthKey, setMonthKey] = useState(currentMonthKey);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [createModal, setCreateModal] = useState(false);
  const [detailRow,   setDetailRow]   = useState(null);

  const canEdit   = myPerms?.includes("QC_EDIT");
  const canDelete = myPerms?.includes("USER_MANAGE");

  useEffect(() => {
    api.fetchFgNcs()
      .then(setRows)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter(r => {
      const matchMonth  = (r.createdAt || "").startsWith(monthKey);
      const matchStatus = statusFilter === "ALL" || r.status === statusFilter;
      const matchQ      = !q ||
        (r.ncNumber    || "").toLowerCase().includes(q) ||
        (r.productName || "").toLowerCase().includes(q) ||
        (r.reportedBy  || "").toLowerCase().includes(q) ||
        (r.batchNumber || "").toLowerCase().includes(q);
      return matchMonth && matchStatus && matchQ;
    });
  }, [rows, search, statusFilter, monthKey]);

  async function handleCreate(body) {
    const created = await api.createFgNc(body);
    setRows(old => [created, ...old]);
    showToast("NC report created");
    setCreateModal(false);
  }

  async function handleProductionSave(body) {
    const updated = await api.updateFgNcProduction(detailRow.id, body);
    setRows(old => old.map(r => r.id === updated.id ? updated : r));
    setDetailRow(updated);
    showToast("Production response saved");
  }

  async function handleClose(body) {
    const updated = await api.closeFgNc(detailRow.id, body);
    setRows(old => old.map(r => r.id === updated.id ? updated : r));
    setDetailRow(updated);
    showToast("NC report closed");
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this NC report? This cannot be undone.")) return;
    await api.deleteFgNc(id);
    setRows(old => old.filter(r => r.id !== id));
    showToast("NC report deleted", "info");
    if (detailRow?.id === id) setDetailRow(null);
  }

  function handleDownloadPdf(row) {
    downloadPdf(`/api/qc/nc/fg/${row.id}/pdf`, row.ncNumber + ".pdf");
  }

  const colCount = 8 + (canDelete ? 1 : 0);

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Non-Conformance — Finished Goods</div>
          <div className="page-subtitle">QF-24 · Track, respond and close internal production non-conformances</div>
        </div>
        {canEdit && (
          <div className="page-header-right">
            <button className="btn btn-primary" onClick={() => setCreateModal(true)}>+ New NC Report</button>
          </div>
        )}
      </div>

      <div className="action-bar" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <MonthYearSelect value={monthKey} onChange={setMonthKey} />
        <input className="form-input" placeholder="Search NC no, product, batch…"
          value={search} onChange={e => setSearch(e.target.value)} style={{ width: 260 }} />
        <select className="form-select" value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)} style={{ width: 200 }}>
          <option value="ALL">All Statuses</option>
          <option value="OPEN">Open</option>
          <option value="PRODUCTION_RESPONDED">Production Responded</option>
          <option value="CLOSED">Closed</option>
        </select>
      </div>

      {loading && <div style={{ color: "#aaa", fontSize: 12 }}>Loading…</div>}
      {error   && <div className="alert-warn">Error: {error}</div>}

      {!loading && !error && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>NC Number</th>
                <th>Product</th>
                <th>Batch</th>
                <th>Test Date</th>
                <th>Due Date</th>
                <th>Status</th>
                <th>Immediate Action</th>
                <th>PDF</th>
                {canDelete && <th></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map(row => (
                <tr key={row.id} style={{ cursor: "pointer" }}
                  onClick={() => setDetailRow(row)}>
                  <td className="fw-600" style={{ fontFamily: "monospace" }}>{row.ncNumber}</td>
                  <td>{row.productName}</td>
                  <td>{row.batchNumber || "—"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{fmtDate(row.testDate)}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{row.dueDate ? fmtDate(row.dueDate) : "—"}</td>
                  <td><StatusBadge status={row.status} /></td>
                  <td style={{ fontSize: 12 }}>
                    {row.disposition ? (DISPOSITION_LABELS[row.disposition] || row.disposition) : "—"}
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <button className="btn-small" onClick={() => handleDownloadPdf(row)}
                      title="Download PDF" style={{ padding: "3px 10px" }}>
                      ⬇ PDF
                    </button>
                  </td>
                  {canDelete && (
                    <td onClick={e => e.stopPropagation()}>
                      <button className="btn-small btn-danger" onClick={() => handleDelete(row.id)}>Del</button>
                    </td>
                  )}
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={colCount} style={{ textAlign: "center", color: "#aaa", padding: 24 }}>
                    No NC reports found. {canEdit && "Click '+ New NC Report' to create one."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {createModal && (
        <div className="overlay" onClick={e => { if (e.target === e.currentTarget) setCreateModal(false); }}>
          <FgNcCreateModal onSave={handleCreate} onCancel={() => setCreateModal(false)} />
        </div>
      )}

      {detailRow && (
        <div className="overlay" onClick={e => { if (e.target === e.currentTarget) setDetailRow(null); }}>
          <FgNcDetailModal
            nc={detailRow}
            canEdit={canEdit}
            onProductionSave={handleProductionSave}
            onClose={handleClose}
            onCancel={() => setDetailRow(null)}
          />
        </div>
      )}
    </>
  );
}
